import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import {
  List,
  Divider,
  IconButton,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  ListSubheader,
  Drawer as MuiDrawer,
} from '@mui/material';
import { Menu as MenuIcon } from '@mui/icons-material';
import { useDispatch, useSelector } from "react-redux";
import { openDeliveritNav, closeDeliveritNav, selectState as selectDeliveritNav } from './deliveritNavSlice';

import { usePermissions } from '../../features/permissions/usePermissions';
import { useFavourites } from '../../features/favourites/useFavourites';
import PinButton from '../../features/favourites/PinButton';

import { ReactComponent as CollapseMenuIcon } from '../../assets/icons/collapse_menu.svg';
import { ReactComponent as DashboardIcon } from '../../assets/icons/dashboard_di.svg';
import { ReactComponent as DeliveryRateIcon } from '../../assets/icons/delivery_rate.svg';
import { ReactComponent as OrderGroupIcon } from '../../assets/icons/order_group.svg';
import { ReactComponent as VendorIcon } from '../../assets/icons/vendor.svg';

/**
 * The DeliverIt sidebar, gated the same way as the ParkIt one.
 *
 * This nav had no gating mechanism before portal permission gating, so all four items were shown to
 * everybody. Kept at module scope and exported so the top nav can decide whether its DeliverIt tab
 * has anywhere to go.
 */
export const deliveritPages = [{
  label: 'Dashboard',
  path: '/deliverit',
  requiredPermission: 'dashboard.deliverit:read',
  icon: <DashboardIcon width={20} height={20} />,
}, {
  label: 'Order Groups',
  path: '/deliverit/order-groups',
  requiredPermission: 'delivery.order:read',
  icon: <OrderGroupIcon width={20} height={20} />,
}, {
  label: 'Delivery Rates',
  path: '/deliverit/delivery-rates',
  requiredPermission: 'delivery.rate:read',
  icon: <DeliveryRateIcon width={20} height={20} />,
}, {
  label: 'Vendors',
  path: '/deliverit/vendors',
  requiredPermission: 'vendor:read',
  icon: <VendorIcon width={20} height={20} />,
}];

export const isDeliveritPageVisible = (page, can = () => true) =>
  !page.requiredPermission || can(page.requiredPermission);

/** Where the DeliverIt tab should point, or null if this person holds nothing in the section. */
export const deliveritLandingPath = (can = () => true) =>
  deliveritPages.find((page) => isDeliveritPageVisible(page, can))?.path ?? null;

const DeliveritNav = () => {
  const { open } = useSelector(selectDeliveritNav);
  const dispatch = useDispatch();
  const { can } = usePermissions();
  const { visible, ready, isPinned, togglePin } = useFavourites();
  const currentPath = useLocation().pathname;
  const [toolbarHeight, setToolbarHeight] = useState(69);

  useEffect(() => {
    const handleResize = () => {
      const toolbar = document.getElementById('appBar');
      if (toolbar) {
        setToolbarHeight(toolbar.offsetHeight);
      }
    };

    // Initial measurement
    handleResize();

    // Listen for resize events
    window.addEventListener('resize', handleResize);

    // Cleanup the event listener
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  const pages = deliveritPages.filter((page) => isDeliveritPageVisible(page, can));
  const pinnedPages = visible.map((item) => pages.find((page) => page.path === item.path)).filter(Boolean);
  const pinnedActive = pinnedPages.some((page) => page.path === currentPath);

  const drawerStyles = (theme) => ({
    backgroundColor: 'var(--sidebar-surface)',
    transition: theme.transitions.create('width', {
      easing: theme.transitions.easing.sharp,
      duration: open ? theme.transitions.duration.enteringScreen : theme.transitions.duration.leavingScreen,
    }),
    overflowX: 'hidden',
    width: open ? 240 : `calc(${theme.spacing(7)} + 1px)`,
    top: toolbarHeight,
    [theme.breakpoints.up('sm')]: {
      width: open ? 240 : `calc(${theme.spacing(8)} + 1px)`,
    },
    '&::-webkit-scrollbar': {
      display: 'none',
    },
  });

  const renderPage = (page, pinnedSection = false) => {
    const active = page.path === currentPath && (pinnedSection || !pinnedActive);
    return (
      <ListItem key={page.path} disablePadding className="group/item relative block">
        <ListItemButton component={Link} to={page.path} sx={{
          minHeight: 48, justifyContent: open ? 'initial' : 'center', pl: 2.5, pr: open ? 5 : 2.5,
          backgroundColor: active ? 'var(--sidebar-active-bg)' : 'transparent',
        }}>
          <ListItemIcon sx={{ minWidth: 0, mr: open ? 3 : 'auto', justifyContent: 'center',
            color: active ? 'var(--sidebar-active-ink)' : 'var(--sidebar-ink-muted)' }}>
            {page.icon}
          </ListItemIcon>
          <ListItemText primary={page.label}
            className={`${active ? 'text-sidebar-active-ink' : 'text-sidebar-ink'} ${open ? 'opacity-100' : 'opacity-0'}`} />
        </ListItemButton>
        {open && <PinButton pinned={isPinned(page.path)} disabled={!ready} label={page.label} onToggle={() => togglePin(page)} />}
      </ListItem>
    );
  };

  return (
    <div>
      <MuiDrawer variant="permanent" open={open} sx={(theme) => ({
        ...drawerStyles(theme), flexShrink: 0, whiteSpace: 'nowrap', boxSizing: 'border-box',
        '& .MuiDrawer-paper': drawerStyles(theme),
      })}>
        <div className="flex items-center p-3 bg-sidebar border-b border-nav-border">
          <IconButton sx={{ color: 'var(--sidebar-ink)' }} onClick={() => open ? dispatch(closeDeliveritNav()) : dispatch(openDeliveritNav())}>
            {open ? (<div className="flex items-center h-[24px]"><CollapseMenuIcon width={18} height={18} /></div>) : (<MenuIcon />)}
          </IconButton>
        </div>
        <Divider />
        {pinnedPages.length > 0 && <div role="group" aria-label="Pinned pages">
          <List subheader={open ? <ListSubheader sx={{ backgroundColor: 'transparent', color: 'var(--sidebar-ink-muted)' }}>Pinned</ListSubheader> : undefined}>
            {pinnedPages.map((page) => renderPage(page, true))}
          </List>
          <Divider />
        </div>}
        <List>
          {pages.map((page) => renderPage(page))}
        </List>
      </MuiDrawer>
    </div>
  );
};

export default DeliveritNav;
